import { Loader2 } from "lucide-react"
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"
import type { CompareMode, ReviewMediaVersion } from "@/components/review/shot-review-media"
import { TaskStatusDot } from "@/components/tasks/task-status"
import { Button, Input, Kbd } from "@/components/ui/primitives"
import { isTypingTarget, SidePeekPager, SidePeekPanel, useSidePeekArrowKeys } from "@/components/ui/side-peek"
import { useTranslation } from "@/hooks/use-translation"
import { formatRelativeTime } from "@/lib/relative-time"
import { taskPersonLabel } from "@/lib/task-board"
import { cn } from "@/lib/utils"
import type { TaskCardRow } from "@/types/tasks"

/** Общие части панелей проверки шота и ассета (docs/shot_review.md, docs/asset_review.md). */

export type CompareState = { mode: CompareMode; ids: number[] } | null

export function last<T>(list: T[], offset = 1): T | undefined {
  return list[list.length - offset]
}

/** Соседи в очереди: элемент вне очереди (открыт по ссылке) идёт перед её началом. */
export function queueNeighbours<T>(queue: T[], index: number) {
  const step = (dir: 1 | -1) => {
    if (queue.length === 0) return null
    const from = index === -1 ? (dir === 1 ? -1 : queue.length) : index
    return queue[from + dir] ?? null
  }
  return { next: step(1), prev: step(-1) }
}

/** A — принять, R — переделать, ← / → — соседний элемент очереди. Пока открыта форма правок, решения не ловим. */
export function useReviewHotkeys({
  canDecide,
  onApprove,
  onRedo,
  onNext,
  onPrev,
}: {
  canDecide: boolean
  onApprove: () => void
  onRedo: () => void
  onNext: (() => void) | null
  onPrev: (() => void) | null
}) {
  useSidePeekArrowKeys(onPrev, onNext)
  useEffect(() => {
    if (!canDecide) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      if (e.key === "a" || e.key === "ф") onApprove()
      else if (e.key === "r" || e.key === "к") onRedo()
      else return
      e.preventDefault()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  })
}

/** Каркас панели: заголовок с позицией в очереди, стрелки, загрузка и ошибка. */
export function ReviewPeek({
  title,
  position,
  projectName,
  onPrev,
  onNext,
  onClose,
  closeOnEscape,
  loading,
  error,
  children,
}: {
  title: string
  position: { index: number; total: number } | null
  projectName: string | null
  onPrev: (() => void) | null
  onNext: (() => void) | null
  onClose: () => void
  closeOnEscape: boolean
  loading: boolean
  error: string | null
  children: ReactNode
}) {
  const { t } = useTranslation("projects")
  return (
    <SidePeekPanel
      title={
        <span className="flex items-center gap-2">
          {title}
          {position ? (
            <span className="text-xs font-normal text-ui-text-muted">{t("review.position", position)}</span>
          ) : null}
          {projectName ? <span className="text-xs font-normal text-ui-text-muted">· {projectName}</span> : null}
        </span>
      }
      closeLabel={t("review.close")}
      onClose={onClose}
      closeOnEscape={closeOnEscape}
      compactHeader
      bodyClassName="flex flex-col gap-4 pb-0"
      actions={
        <SidePeekPager prevLabel={t("review.prev")} nextLabel={t("review.next")} onPrev={onPrev} onNext={onNext} />
      }
    >
      {error ? (
        <p className="text-sm text-ui-danger">{error}</p>
      ) : loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-ui-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {t("review.loading")}
        </div>
      ) : (
        children
      )}
    </SidePeekPanel>
  )
}

/** Вкладки слотов («Кадр / Видео», «Вид 3/4 / Север») и статус проверки справа. */
export function ReviewTabs<K extends string>({
  tabs,
  active,
  onSelect,
  status,
}: {
  tabs: { key: K; label: string }[]
  active: K
  onSelect: (key: K) => void
  status: string
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          onClick={() => onSelect(tab.key)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs",
            active === tab.key
              ? "bg-ui-surface-raised text-ui-text-primary"
              : "text-ui-text-muted hover:text-ui-text-primary",
          )}
        >
          {tab.label}
        </button>
      ))}
      <span className="ml-auto self-center text-xs text-ui-text-muted">{status}</span>
    </div>
  )
}

/** Выбор версии для сравнения: снять, если уже выбрана, иначе добавить — в выборе остаются две последние. */
export function toggleCompareId(compare: NonNullable<CompareState>, id: number): CompareState {
  const ids = compare.ids.includes(id) ? compare.ids.filter((x) => x !== id) : [...compare.ids, id].slice(-2)
  return { ...compare, ids }
}

/**
 * Управление сравнением версий — одно в ленте и на вкладках версий шота: «Рядом / Шторка» (пока сравниваем) и
 * «сравнить» / «готово». `startIds` — что сравнить сразу, обычно две последние версии.
 */
export function VersionCompareControls({
  compare,
  startIds,
  onCompareChange,
}: {
  compare: CompareState
  startIds: number[]
  onCompareChange: (next: CompareState) => void
}) {
  const { t } = useTranslation("projects")
  if (!compare && startIds.length < 2) return null
  return (
    <>
      {compare ? (
        <span className="flex gap-1 text-xs">
          {(["side", "wipe"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onCompareChange({ ...compare, mode })}
              className={cn(
                "rounded-sm px-1.5 py-0.5",
                compare.mode === mode ? "bg-ui-surface-raised text-ui-text-primary" : "text-ui-text-muted",
              )}
            >
              {t(`review.compare.${mode}`)}
            </button>
          ))}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => onCompareChange(compare ? null : { mode: "side", ids: startIds.slice(0, 2) })}
        className="ml-auto text-xs text-ui-link hover:text-ui-accent-text"
      >
        {compare ? t("review.compare.done") : t("review.compare.start")}
      </button>
    </>
  )
}

/** Полоса версий: выбор показанной или двух для сравнения, режимы «Рядом / Шторка». */
export function ReviewVersionStrip({
  title,
  versions,
  shownId,
  compare,
  onCompareChange,
  onSelect,
}: {
  title: string
  versions: (ReviewMediaVersion & { created_at: string })[]
  shownId: number | null
  compare: CompareState
  onCompareChange: (next: CompareState) => void
  onSelect: (id: number) => void
}) {
  const { t, locale } = useTranslation("projects")
  const startIds = [last(versions, 2)?.id, last(versions)?.id].filter((x): x is number => x != null)
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <h3 className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">{title}</h3>
        <VersionCompareControls compare={compare} startIds={startIds} onCompareChange={onCompareChange} />
      </div>
      {compare ? <p className="text-xs text-ui-text-muted">{t("review.compare.hint")}</p> : null}
      <div className="it2tv-scrollbar flex gap-2 overflow-x-auto pb-1">
        {versions.map((v) => {
          const picked = compare ? compare.ids.includes(v.id) : v.id === shownId
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => (compare ? onCompareChange(toggleCompareId(compare, v.id)) : onSelect(v.id))}
              className="flex w-24 shrink-0 flex-col gap-1 text-left"
            >
              <span
                className={cn(
                  "relative block aspect-video overflow-hidden rounded-sm border-2 bg-ui-surface-sunken",
                  picked ? "border-ui-accent" : "border-transparent",
                )}
              >
                {v.thumb_url ? <img src={v.thumb_url} alt="" className="h-full w-full object-cover" /> : null}
                <span className="absolute top-0.5 left-0.5 rounded-sm bg-black/60 px-1 text-[10px] text-white">
                  v{v.number}
                </span>
              </span>
              <span className="truncate text-[11px] text-ui-text-muted">
                {v.active ? t("review.current") : formatRelativeTime(v.created_at, locale)}
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

/**
 * Задачи по шоту / ассету. Задача открывается панелью трекера прямо в ленте (onOpenTask), «+ задача» заводит новую
 * про этот шот или ассет в любом статусе — например, если принятому надо что-то добавить. Задачи из проверки
 * закроются при «Принять».
 */
export function ReviewTasks({
  title,
  empty,
  tasks,
  closesOnApprove,
  onOpenTask,
  onCreate,
}: {
  title: string
  empty: string
  tasks: TaskCardRow[]
  closesOnApprove: (task: TaskCardRow) => boolean
  onOpenTask: (task: TaskCardRow) => void
  /** null — заводить задачи нельзя (нет прав на контент проекта). */
  onCreate: ((title: string) => Promise<void>) | null
}) {
  const { t } = useTranslation(["projects", "tasks"])
  const [draft, setDraft] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    const value = draft?.trim()
    if (!value || !onCreate || saving) return
    setSaving(true)
    try {
      await onCreate(value)
      setDraft(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <h3 className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">{title}</h3>
        {onCreate && draft == null ? (
          <button
            type="button"
            onClick={() => setDraft("")}
            className="ml-auto text-xs text-ui-link hover:text-ui-accent-text"
          >
            {t("review.tasks.add")}
          </button>
        ) : null}
      </div>
      {draft != null ? (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault()
                e.stopPropagation()
                setDraft(null)
              }
            }}
            placeholder={t("review.tasks.newPlaceholder")}
            aria-label={t("review.tasks.newPlaceholder")}
            className="h-8 text-xs"
          />
          <Button type="submit" size="sm" className="h-8 shrink-0 px-3" disabled={saving || !draft.trim()}>
            {t("review.tasks.create")}
          </Button>
        </form>
      ) : null}
      {tasks.length === 0 && draft == null ? (
        <p className="text-xs text-ui-text-muted">{empty}</p>
      ) : (
        tasks.map((task) => (
          <button
            key={task.id}
            type="button"
            onClick={() => onOpenTask(task)}
            className="flex items-center gap-3 rounded-md border border-ui-border px-3 py-2 text-left hover:bg-ui-surface-raised"
          >
            <span className="flex w-28 shrink-0 items-center gap-1.5 text-[11px] text-ui-text-secondary">
              <TaskStatusDot status={task.status} />
              {t(`tasks:columns.${task.status}`)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] text-ui-text-primary">{`${task.key} · ${task.title}`}</span>
              <span className="block truncate text-[11px] text-ui-text-muted">
                {task.assignee ? taskPersonLabel(task.assignee, t("tasks:unnamed")) : t("tasks:dialog.noAssignee")}
                {task.comments_count > 0 ? ` · ${t("review.tasks.comments", { count: task.comments_count })}` : ""}
              </span>
            </span>
            {closesOnApprove(task) && task.status !== "done" ? (
              <span className="shrink-0 text-[11px] text-ui-text-muted">{t("review.tasks.closesOnApprove")}</span>
            ) : null}
          </button>
        ))
      )}
    </section>
  )
}

export function ReviewFooter({
  busy,
  canApprove,
  onRedo,
  onApprove,
}: {
  busy: boolean
  canApprove: boolean
  onRedo: () => void
  onApprove: () => void
}) {
  const { t } = useTranslation("projects")
  return (
    <div className="sticky bottom-0 -mx-5 mt-auto flex items-center gap-2 border-t border-ui-border bg-ui-bg px-5 py-3">
      <Button size="sm" variant="secondary" onClick={onRedo} disabled={busy}>
        {t("review.redoAction")}
        <Kbd>R</Kbd>
      </Button>
      <Button size="sm" className="ml-auto" onClick={onApprove} disabled={busy || !canApprove}>
        {t("review.approveAction")}
        <Kbd>A</Kbd>
      </Button>
    </div>
  )
}

export function ReviewQueueStrip({
  title,
  items,
}: {
  title: string
  items: { key: string; label: string; thumbUrl: string | null; current: boolean; onClick: () => void }[]
}) {
  if (items.length < 2) return null
  return (
    <div className="-mx-5 border-t border-ui-border px-5 py-2.5">
      <p className="mb-1.5 text-[11px] text-ui-text-muted">{title}</p>
      <div className="it2tv-scrollbar flex gap-1.5 overflow-x-auto">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            title={item.label}
            onClick={item.onClick}
            className={cn(
              "block h-9 w-16 shrink-0 overflow-hidden rounded-sm border-2 bg-ui-surface-sunken",
              item.current ? "border-ui-accent" : "border-transparent opacity-70 hover:opacity-100",
            )}
          >
            {item.thumbUrl ? <img src={item.thumbUrl} alt="" className="h-full w-full object-cover" /> : null}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Длинный текст (промпт) в три строки и ссылка «раскрыть промпт» под ним — только если текст правда не влез.
 * Ссылка стоит отдельной строкой: внутри обрезанного блока line-clamp её прятал.
 */
export function ExpandablePrompt({ text }: { text: string }) {
  const { t } = useTranslation("projects")
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [clipped, setClipped] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    // Измеряем только свёрнутый текст: раскрытый всегда «влезает».
    if (el && !open) setClipped(el.scrollHeight > el.clientHeight + 1)
  }, [text, open])

  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      <span ref={ref} className={cn("break-words whitespace-pre-wrap text-ui-text-secondary", !open && "line-clamp-3")}>
        {text}
      </span>
      {clipped || open ? (
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-ui-link hover:text-ui-accent-text">
          {open ? t("review.promptCollapse") : t("review.promptExpand")}
        </button>
      ) : null}
    </span>
  )
}
